import { Fragment, useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import Select from 'react-select';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { setAlert } from '../../actions/alert';
import { changeLoader } from '../../actions/loader';

const ENDPOINT_ACEPTAR = '/facturas/aceptar';
const ENDPOINT_CANCELAR = '/facturas/cancelar';

const ENDPOINT_FACTURA_DETALLES = '/facturas/detalles';

const ENDPOINT_AREAS = '/configuracion/listar_areas';
const ENDPOINT_ORGANIZADORES = '/configuracion/listar_organizadores';
const ENDPOINT_UBICACIONES = '/configuracion/listar_ubicaciones';

const onlyNumber = (v) => (v ?? '').toString().replace(/[^\d.]/g, '');

const getUnidadNombre = (p) => {
    return p?.unidades?.nombre || p?.unidad?.nombre || '';
};

const formatCantidadConUnidad = (qty, unidadNombre) => {
    if (qty == null) return '—';
    const u = (unidadNombre || '').trim();
    return `${qty}${u ? ` ${u}` : ''}`;
};

const ModalCambiarStatusFactura = ({ show, factura, onClose, onSuccess }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [nuevoStatus, setNuevoStatus] = useState(null);

    const [detalleRes, setDetalleRes] = useState(null);
    const [cargandoDetalle, setCargandoDetalle] = useState(false);

    const [areas, setAreas] = useState([]);
    const [organizadores, setOrganizadores] = useState([]);
    const [ubicaciones, setUbicaciones] = useState([]);

    const [prodSel, setProdSel] = useState(null);
    const [areaSel, setAreaSel] = useState(null);
    const [orgSel, setOrgSel] = useState(null);
    const [ubiSel, setUbiSel] = useState(null);
    const [cantidad, setCantidad] = useState('');

    const [asignaciones, setAsignaciones] = useState([]);

    const opcionesStatus = useMemo(() => ([
        { value: 2, label: 'Recibir' },
        { value: 3, label: 'Cancelada' },
        { value: 4, label: 'Devolución' },
    ]), []);

    const facturaDetalle = useMemo(() => detalleRes?.factura || null, [detalleRes]);

    const productosDetalle = useMemo(() => {
        const src = facturaDetalle?.productos || [];
        if (!Array.isArray(src)) return [];

        return src
            .map(p => {
                const cantidadSurtir = Number(p?.cantidad_surtir ?? 0);

                /* El backend siempre manda cantidad_sin_asignar = cantidad_surtir y
                   cantidad_asignada = 0 en /detalles, sin descontar rondas de recepción previas.
                   Lo real ya recibido se calcula aquí sumando los movimientos registrados. */
                const movimientos = Array.isArray(p?.movimientos) ? p.movimientos : [];
                const yaRecibido = movimientos.reduce((acc, m) => acc + Number(m?.cantidad || 0), 0);
                const pendiente = Math.max(0, cantidadSurtir - yaRecibido);

                return {
                    _id: p?._id,
                    nombre: p?.nombre || 'Producto',
                    cantidad_surtir: cantidadSurtir,
                    precio: Number(p?.precio_unitario ?? p?.precio ?? 0),
                    impuestos: Number(p?.impuestos ?? 0),
                    cantidad_sin_asignar: pendiente,
                    cantidad_asignada: yaRecibido,
                    codigo_tienda: p?.codigo_tienda || '',
                    codigo_proveedor: p?.codigo_proveedor || '',
                    unidad_nombre: getUnidadNombre(p),
                };
            })
            .filter(p => p._id);
    }, [facturaDetalle]);

    const opcionesProductos = useMemo(() => {
        return productosDetalle.map(p => ({
            value: p._id,
            label: p.nombre,
            raw: p,
        }));
    }, [productosDetalle]);

    const opcionesAreas = useMemo(() => {
        return (areas || [])
            .filter(a => a?._id)
            .map(a => ({
                value: a._id,
                label: a.nombre || 'Área',
                raw: a,
            }));
    }, [areas]);

    const opcionesOrganizadores = useMemo(() => {
        return (organizadores || []).map(o => ({
            value: o._id,
            label: o.nombre || 'Organizador',
            raw: o,
        }));
    }, [organizadores]);

    const opcionesUbicaciones = useMemo(() => {
        return (ubicaciones || []).map(u => ({
            value: u._id,
            label: u.nombre || 'Ubicación',
            raw: u,
        }));
    }, [ubicaciones]);

    const remainingByProducto = useMemo(() => {
        const map = {};
        productosDetalle.forEach(p => { map[p._id] = p.cantidad_sin_asignar; });

        asignaciones.forEach(a => {
            if (map[a.id_producto] != null) map[a.id_producto] -= Number(a.cantidad || 0);
        });

        Object.keys(map).forEach(k => {
            if (map[k] < 0) map[k] = 0;
        });

        return map;
    }, [productosDetalle, asignaciones]);

    const todoAsignado = useMemo(() => {
        if (!productosDetalle.length) return false;
        return productosDetalle.every(p => (remainingByProducto[p._id] ?? 0) === 0);
    }, [productosDetalle, remainingByProducto]);

    /* Status que resultará de guardar esta ronda: si no se cubre todo lo pendiente, la factura
       queda (o se mantiene) Parcialmente recibida (1); si se cubre todo, Recibida (2). El backend
       permite repetir rondas parciales (0→1, 1→1, 1→2), solo rechaza bajar el status. */
    const statusResultante = todoAsignado ? 2 : 1;

    const puedeGuardarRecepcion = !!productosDetalle.length && !!asignaciones.length;

    const tituloFactura = useMemo(() => {
        const num = factura?.numero || 'Sin número';
        return `${num}`;
    }, [factura]);

    const onChangeStatus = (opt) => {
        setNuevoStatus(opt);

        if (Number(opt?.value) === 4) {
            if (!factura?._id) {
                dispatch(setAlert('No se encontró el ID de la factura para editar.', 'warning'));
                return;
            }
            onClose?.();
            navigate(`/facturas/editar/${factura._id}`);
        }
    };

    useEffect(() => {
        if (!show) return;

        setNuevoStatus(null);
        setDetalleRes(null);
        setAsignaciones([]);

        setProdSel(null);
        setAreaSel(null);
        setOrgSel(null);
        setUbiSel(null);
        setCantidad('');

        setOrganizadores([]);
        setUbicaciones([]);

        cargarAreas();
    }, [show]);

    useEffect(() => {
        if (!show) return;
        if (Number(nuevoStatus?.value) !== 2) return;
        if (!factura?._id) return;

        cargarDetalleFactura(factura._id);
    }, [show, nuevoStatus, factura?._id]);

    const cargarAreas = async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_ROUTE}${ENDPOINT_AREAS}`);
            const listado = res?.data?.areas || [];
            setAreas(Array.isArray(listado) ? listado : []);
        } catch (e) {
            setAreas([]);
        }
    };

    const cargarOrganizadores = async (id_area) => {
        try {
            const params = new URLSearchParams();
            params.append('id', id_area);

            const res = await axios.post(
                `${process.env.REACT_APP_API_ROUTE}${ENDPOINT_ORGANIZADORES}`,
                params,
                { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
            );

            const listado = res?.data?.organizadores || [];
            setOrganizadores(Array.isArray(listado) ? listado : []);
            return Array.isArray(listado) ? listado : [];
        } catch (e) {
            setOrganizadores([]);
            return [];
        }
    };

    const cargarUbicaciones = async (id_organizador) => {
        try {
            const params = new URLSearchParams();
            params.append('id', id_organizador);

            const res = await axios.post(
                `${process.env.REACT_APP_API_ROUTE}${ENDPOINT_UBICACIONES}`,
                params,
                { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
            );

            const listado = res?.data?.ubicaciones || [];
            setUbicaciones(Array.isArray(listado) ? listado : []);
            return Array.isArray(listado) ? listado : [];
        } catch (e) {
            setUbicaciones([]);
            return [];
        }
    };

    const cargarDetalleFactura = async (id_factura) => {
        try {
            setCargandoDetalle(true);

            const params = new URLSearchParams();
            params.append('id', id_factura);

            const res = await axios.post(
                `${process.env.REACT_APP_API_ROUTE}${ENDPOINT_FACTURA_DETALLES}`,
                params,
                { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
            );

            setDetalleRes(res?.data || null);
        } catch (e) {
            setDetalleRes(null);
            dispatch(setAlert('No se pudo cargar el detalle de la factura.', 'danger'));
        } finally {
            setCargandoDetalle(false);
        }
    };

    const onChangeProducto = (opt) => {
        setProdSel(opt);

        const id = opt?.value;
        if (!id) return setCantidad('');

        const rem = remainingByProducto[id] ?? 0;
        setCantidad(rem > 0 ? String(rem) : '0');
    };

    const onChangeArea = async (opt) => {
        setAreaSel(opt);
        setOrgSel(null);
        setUbiSel(null);

        setOrganizadores([]);
        setUbicaciones([]);

        if (!opt?.value) return;

        if ((opt?.raw?.organizadores ?? 0) > 0) {
            await cargarOrganizadores(opt.value);
        }
    };

    const onChangeOrganizador = async (opt) => {
        setOrgSel(opt);
        setUbiSel(null);

        setUbicaciones([]);

        if (!opt?.value) return;

        if ((opt?.raw?.ubicaciones ?? 0) > 0) {
            await cargarUbicaciones(opt.value);
        }
    };

    const puedeAgregarAsignacion = () => {
        if (!prodSel?.value) return false;
        if (!areaSel?.value) return false;

        const qty = Number(cantidad);
        if (!qty || qty <= 0) return false;

        const rem = remainingByProducto[prodSel.value] ?? 0;
        if (qty > rem) return false;

        if (ubiSel?.value && !orgSel?.value) return false;

        return true;
    };

    const onAgregarAsignacion = () => {
        if (!puedeAgregarAsignacion()) {
            return dispatch(setAlert('Completa producto, área y una cantidad válida. Organizador y ubicación son opcionales.', 'warning'));
        }

        const p = prodSel.raw;
        const qty = Number(cantidad);

        const item = {
            id_producto: prodSel.value,
            nombre: prodSel.label,
            precio: Number(p?.precio ?? 0),
            unidad_nombre: p?.unidad_nombre || '',

            area: areaSel.value,
            area_nombre: areaSel.label,

            organizador: orgSel?.value ?? null,
            organizador_nombre: orgSel?.label ?? null,

            ubicacion: ubiSel?.value ?? null,
            ubicacion_nombre: ubiSel?.label ?? null,

            cantidad: qty,
        };

        setAsignaciones(prev => [...prev, item]);

        const rem = (remainingByProducto[prodSel.value] ?? 0) - qty;
        setCantidad(rem > 0 ? String(rem) : '0');
    };

    const onEliminarAsignacion = (idx) => {
        setAsignaciones(prev => prev.filter((_, i) => i !== idx));
    };

    const onCancelarFactura = async () => {
        if (!factura?._id) return;

        try {
            await dispatch(changeLoader(true));

            const params = new URLSearchParams();
            params.append('id', factura._id);

            await axios.post(
                `${process.env.REACT_APP_API_ROUTE}${ENDPOINT_CANCELAR}`,
                params,
                { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
            );

            dispatch(setAlert('Factura cancelada correctamente', 'success'));
            await dispatch(changeLoader(false));

            onSuccess?.();
        } catch (e) {
            await dispatch(changeLoader(false));
            dispatch(setAlert(e?.response?.data?.msg || 'Error al cancelar la factura', 'danger'));
        }
    };

    const onAceptarFactura = async () => {
        if (!factura?._id) return;

        if (!productosDetalle.length) {
            return dispatch(setAlert('No hay productos para acomodar.', 'warning'));
        }

        if (!asignaciones.length) {
            return dispatch(setAlert('Agrega al menos un acomodo antes de guardar.', 'warning'));
        }

        const statusDestino = statusResultante;

        try {
            await dispatch(changeLoader(true));

            const grouped = {};
            asignaciones.forEach(a => {
                if (!grouped[a.id_producto]) {
                    grouped[a.id_producto] = {
                        _id: a.id_producto,
                        precio: a.precio,
                        ubicaciones: [],
                    };
                }

                grouped[a.id_producto].ubicaciones.push({
                    area: a.area,
                    organizador: a.organizador ?? null,
                    ubicacion: a.ubicacion ?? null,
                    cantidad: a.cantidad,
                });
            });

            const productosPayload = Object.values(grouped);

            const params = new URLSearchParams();
            params.append('id', factura._id);
            params.append('status', statusDestino);

            productosPayload.forEach((p, i) => {
                params.append(`productos[${i}][_id]`, p._id);
                params.append(`productos[${i}][precio]`, p.precio);

                (p.ubicaciones || []).forEach((u, j) => {
                    params.append(`productos[${i}][ubicaciones][${j}][area]`, u.area);
                    params.append(`productos[${i}][ubicaciones][${j}][organizador]`, (u.organizador ?? '') || '');
                    params.append(`productos[${i}][ubicaciones][${j}][ubicacion]`, (u.ubicacion ?? '') || '');
                    params.append(`productos[${i}][ubicaciones][${j}][cantidad]`, u.cantidad);
                });
            });

            await axios.post(
                `${process.env.REACT_APP_API_ROUTE}${ENDPOINT_ACEPTAR}`,
                params,
                { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
            );

            dispatch(setAlert(
                statusDestino === 1 ? 'Factura marcada como Parcialmente recibida' : 'Factura aceptada correctamente',
                'success'
            ));
            await dispatch(changeLoader(false));

            onSuccess?.();
        } catch (e) {
            await dispatch(changeLoader(false));
            dispatch(setAlert(e?.response?.data?.msg || 'Error al aceptar la factura', 'danger'));
        }
    };

    return (
        <Modal show={show} onHide={onClose} centered size={Number(nuevoStatus?.value) === 2 ? 'xl' : 'md'}>
            <Modal.Header closeButton>
                <Modal.Title>Cambiar status - {tituloFactura}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <div className="mb-3">
                    <label className="form-label">Nuevo status</label>
                    <Select
                        options={opcionesStatus}
                        value={nuevoStatus}
                        onChange={onChangeStatus}
                        isClearable
                        placeholder="Selecciona una opción..."
                        noOptionsMessage={() => 'Sin resultados'}
                    />
                </div>

                {Number(nuevoStatus?.value) === 3 && (
                    <div className="alert alert-danger">
                        Esta acción marcará la factura como <b>Eliminada</b>. Se mantendrá visible en el listado.
                    </div>
                )}

                {Number(nuevoStatus?.value) === 2 && (
                    <Fragment>
                        {todoAsignado ? (
                            <div className="alert alert-warning">
                                <b>Importante:</b> con lo acomodado hasta ahora la factura quedará como <b>Recibida</b> — ya no se
                                podrá editar ni eliminar, porque los productos entran a inventario y pudieron haber salido en ventas.
                            </div>
                        ) : (
                            <div className="alert alert-info">
                                No es necesario acomodar todo lo pendiente: lo que no asignes ahora se podrá recibir después en otra
                                ronda. Con lo acomodado hasta ahora, la factura quedará como <b>Parcialmente recibida</b>.
                            </div>
                        )}

                        <div className="card border-0 shadow-none">
                            <div className="card-header bg-transparent border-0 px-0">
                                <h6 className="mb-0">Acomodar productos en ubicación física</h6>
                            </div>

                            <div className="card-body px-0 pt-2">
                                {cargandoDetalle && (
                                    <div className="alert alert-info">Cargando detalle de productos...</div>
                                )}

                                {!cargandoDetalle && !!productosDetalle.length && (
                                    <Fragment>
                                        <div className="table-responsive mb-3">
                                            <table className="table table-sm table-hover">
                                                <thead>
                                                    <tr>
                                                        <th>Producto</th>
                                                        <th className="text-end">Cantidad factura</th>
                                                        <th className="text-end">Asignada</th>
                                                        <th className="text-end">Por asignar</th>
                                                        <th className="text-end">Precio</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {productosDetalle.map(p => (
                                                        <tr key={p._id}>
                                                            <td>{p.nombre}</td>
                                                            <td className="text-end">{formatCantidadConUnidad(p.cantidad_surtir, p.unidad_nombre)}</td>
                                                            <td className="text-end">{formatCantidadConUnidad(p.cantidad_asignada, p.unidad_nombre)}</td>
                                                            <td className="text-end">
                                                                <span className={((remainingByProducto[p._id] ?? 0) === 0) ? 'text-success' : 'text-warning'}>
                                                                    {formatCantidadConUnidad((remainingByProducto[p._id] ?? 0), p.unidad_nombre)}
                                                                </span>
                                                            </td>
                                                            <td className="text-end">{p.precio}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>

                                        <div className="row">
                                            <div className="col-12 col-md-5 mb-3">
                                                <label className="form-label">Producto</label>
                                                <Select
                                                    options={opcionesProductos}
                                                    value={prodSel}
                                                    onChange={onChangeProducto}
                                                    isClearable
                                                    placeholder="Selecciona un producto..."
                                                />
                                            </div>

                                            <div className="col-12 col-md-3 mb-3">
                                                <label className="form-label">Cantidad</label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    value={cantidad}
                                                    onChange={(e) => setCantidad(onlyNumber(e.target.value))}
                                                    disabled={!prodSel}
                                                />
                                                {prodSel?.value && (
                                                    <small className="text-muted">
                                                        Disponible por asignar: {formatCantidadConUnidad((remainingByProducto[prodSel.value] ?? 0), prodSel?.raw?.unidad_nombre || '')}
                                                    </small>
                                                )}
                                            </div>

                                            <div className="col-12 col-md-4 mb-3">
                                                <label className="form-label">Área</label>
                                                <Select
                                                    options={opcionesAreas}
                                                    value={areaSel}
                                                    onChange={onChangeArea}
                                                    isClearable
                                                    placeholder="Selecciona un área..."
                                                />
                                            </div>

                                            <div className="col-12 col-md-6 mb-3">
                                                <label className="form-label">Organizador (opcional)</label>
                                                <Select
                                                    options={opcionesOrganizadores}
                                                    value={orgSel}
                                                    onChange={onChangeOrganizador}
                                                    isClearable
                                                    placeholder={(areaSel?.raw?.organizadores ?? 0) > 0 ? 'Selecciona un organizador (opcional)...' : 'No aplica'}
                                                    isDisabled={!areaSel || (areaSel?.raw?.organizadores ?? 0) === 0}
                                                />
                                            </div>

                                            <div className="col-12 col-md-6 mb-3">
                                                <label className="form-label">Ubicación (opcional)</label>
                                                <Select
                                                    options={opcionesUbicaciones}
                                                    value={ubiSel}
                                                    onChange={setUbiSel}
                                                    isClearable
                                                    placeholder={(orgSel?.raw?.ubicaciones ?? 0) > 0 ? 'Selecciona una ubicación (opcional)...' : 'No aplica'}
                                                    isDisabled={
                                                        !areaSel ||
                                                        !orgSel ||
                                                        (orgSel?.raw?.ubicaciones ?? 0) === 0
                                                    }
                                                />
                                            </div>

                                            <div className="col-12 d-flex justify-content-end">
                                                <button type="button" className="btn btn-success" onClick={onAgregarAsignacion}>
                                                    <i className="fa fa-plus" /> Agregar acomodo
                                                </button>
                                            </div>
                                        </div>

                                        <div className="table-responsive mt-3">
                                            <table className="table table-striped table-hover">
                                                <thead>
                                                    <tr>
                                                        <th>Producto</th>
                                                        <th>Área</th>
                                                        <th>Organizador</th>
                                                        <th>Ubicación</th>
                                                        <th className="text-end">Cantidad</th>
                                                        <th className="text-end">Opciones</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {!asignaciones.length && (
                                                        <tr>
                                                            <td colSpan="6" className="text-center">Sin acomodos agregados</td>
                                                        </tr>
                                                    )}

                                                    {asignaciones.map((a, idx) => (
                                                        <tr key={`${a.id_producto}-${idx}`}>
                                                            <td>{a.nombre}</td>
                                                            <td>{a.area_nombre || (opcionesAreas.find(x => x.value === a.area)?.label || a.area)}</td>
                                                            <td>{a.organizador_nombre || '—'}</td>
                                                            <td>{a.ubicacion_nombre || '—'}</td>
                                                            <td className="text-end">{formatCantidadConUnidad(a.cantidad, a.unidad_nombre)}</td>
                                                            <td className="text-end">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-link"
                                                                    title="Eliminar"
                                                                    onClick={() => onEliminarAsignacion(idx)}
                                                                >
                                                                    <i className="fas fa-trash text-danger" />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>

                                    </Fragment>
                                )}

                                {!cargandoDetalle && !productosDetalle.length && (
                                    <div className="alert alert-warning mb-0">
                                        No se encontró detalle de productos para esta factura.
                                    </div>
                                )}
                            </div>
                        </div>
                    </Fragment>
                )}
            </Modal.Body>

            <Modal.Footer>
                <button type="button" className="btn btn-secondary" onClick={onClose}>
                    Cerrar
                </button>

                {Number(nuevoStatus?.value) === 3 && (
                    <button type="button" className="btn btn-danger" onClick={onCancelarFactura}>
                        Cancelar factura
                    </button>
                )}

                {Number(nuevoStatus?.value) === 2 && (
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={onAceptarFactura}
                        disabled={!puedeGuardarRecepcion}
                        title={!asignaciones.length ? 'Agrega al menos un acomodo antes de guardar' : 'Guardar'}
                    >
                        {statusResultante === 1 ? 'Guardar (Parcialmente recibida)' : 'Guardar (Recibida)'}
                    </button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default ModalCambiarStatusFactura;
